import React, { useState, useEffect, useRef, useCallback } from 'react';
import './NumeroSuerte.css';

const PUB = process.env.PUBLIC_URL;
const ALTO_DIGITO = 76; // px, alto de cada dígito en el tambor
const VUELTAS = 3;      // vueltas completas antes de frenar

// Tabla de los sueños de la quiniela (tradicional, 00-99)
const TABLA_SUENOS = [
  'los huevos', 'el agua', 'el niño', 'San Cono', 'la cama', 'el gato', 'el perro', 'el revólver', 'el incendio', 'el arroyo',
  'la leche', 'el minero', 'el soldado', 'la yeta', 'el borracho', 'la niña bonita', 'el anillo', 'la desgracia', 'la sangre', 'el pescado',
  'la fiesta', 'la mujer', 'el loco', 'el cocinero', 'el caballo', 'la gallina', 'la misa', 'el peine', 'el cerro', 'San Pedro',
  'Santa Rosa', 'la luz', 'el dinero', 'la edad de Cristo', 'la cabeza', 'el pajarito', 'la manteca', 'el dentista', 'las piedras', 'la lluvia',
  'el cura', 'el cuchillo', 'las zapatillas', 'el balcón', 'la cárcel', 'el vino', 'los tomates', 'el muerto', 'el muerto que habla', 'la carne',
  'el pan', 'el serrucho', 'madre e hijo', 'el barco', 'la vaca', 'la música', 'la caída', 'el jorobado', 'el ahogado', 'las plantas',
  'la virgen', 'la escopeta', 'la inundación', 'el casamiento', 'el llanto', 'el cazador', 'la lombriz', 'la mordida', 'los sobrinos', 'los vicios',
  'el sueño', 'el excremento', 'la sorpresa', 'el hospital', 'la escalera', 'los besos', 'las llamas', 'las piernas', 'la ramera', 'el ladrón',
  'la bocha', 'las flores', 'la pelea', 'el mal tiempo', 'la iglesia', 'la linterna', 'el humo', 'los piojos', 'el Papa', 'la rata',
  'el miedo', 'el excusado', 'el médico', 'el enamorado', 'el cementerio', 'los anteojos', 'el marido', 'la mesa', 'el lavadero', 'el hermano',
];

const VARIANTES = ['palanca', 'tambor', 'cospel'];

const movimientoReducido = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Dígito que cambia solo, como la máquina en reposo.
// Se frena cuando el disparador no está a la vista para no gastar timers.
function useDigitoEnReposo(activo) {
  const [digito, setDigito] = useState(7);

  useEffect(() => {
    if (!activo || movimientoReducido()) return undefined;
    const id = setInterval(() => {
      setDigito((d) => (d + 1 + Math.floor(Math.random() * 3)) % 10);
    }, 3200);
    return () => clearInterval(id);
  }, [activo]);

  return digito;
}

/* ─────────────────────────── La máquina ───────────────────────────
   Misma mecánica de siempre: 4 tambores manejados por refs (reset arriba
   sin transición + giro completo con transición), así el giro es total
   aunque se repita el dígito.                                              */
function Maquina({ girarAlAbrir }) {
  const [girando, setGirando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const stripRefs = useRef([]);
  const timerRef = useRef(null);

  const posicionar = (strip, digito, conTransicion, i) => {
    strip.style.transition = conTransicion
      ? `transform ${1.3 + i * 0.4}s cubic-bezier(.15,.85,.35,1.02)`
      : 'none';
    strip.style.transform = `translateY(-${(VUELTAS * 10 + digito) * ALTO_DIGITO}px)`;
  };

  const girar = useCallback(() => {
    setGirando((yaGirando) => {
      if (yaGirando) return yaGirando;

      const nuevos = Array.from({ length: 4 }, () => Math.floor(Math.random() * 10));
      setResultado(null);

      if (movimientoReducido()) {
        stripRefs.current.forEach((s, i) => { if (s) posicionar(s, nuevos[i], false, i); });
        setResultado(nuevos);
        return false;
      }

      stripRefs.current.forEach((s) => {
        if (s) { s.style.transition = 'none'; s.style.transform = 'translateY(0)'; }
      });
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          stripRefs.current.forEach((s, i) => { if (s) posicionar(s, nuevos[i], true, i); });
        });
      });

      // El último tambor frena a los ~2.5s; después mostramos el significado
      timerRef.current = setTimeout(() => {
        setGirando(false);
        setResultado(nuevos);
      }, 2700);

      return true;
    });
  }, []);

  // Posición inicial 7777 y, si vino de tirar la palanca, arranca girando.
  useEffect(() => {
    stripRefs.current.forEach((s, i) => { if (s) posicionar(s, 7, false, i); });
    if (girarAlAbrir) {
      const id = setTimeout(girar, 260);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [girarAlAbrir, girar]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const numero = resultado ? resultado.join('') : null;
  const dosUltimos = resultado ? (resultado[2] * 10 + resultado[3]) : null;

  return (
    <div className="hc-slot ns-slot">
      <div className={`hc-maquina-real${girando ? ' girando' : ''}`}>
        <img
          className="hc-maq-img"
          src={`${PUB}/img/juegos/maquina.png`}
          alt="Máquina tragamonedas de la suerte"
          loading="lazy"
        />
        <div className="hc-maq-placa" aria-hidden="true">Máquina de la suerte</div>
        <div className="hc-maq-ventana">
          <div className="hc-reels" aria-label={numero ? `Número generado: ${numero}` : 'Máquina de números'}>
            {[0, 1, 2, 3].map((i) => (
              <div className="hc-reel" key={i}>
                <div className="hc-reel-strip" ref={(el) => { stripRefs.current[i] = el; }}>
                  {Array.from({ length: VUELTAS * 10 + 10 }, (_, k) => (
                    <span key={k} className="hc-reel-digit">{k % 10}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        <button
          className="hc-maq-hotspot"
          onClick={girar}
          disabled={girando}
          aria-label="Tirar de la palanca"
        />
      </div>

      <button className="hc-btn hc-btn-gold hc-slot-btn" onClick={girar} disabled={girando}>
        {girando ? 'Girando…' : 'Girar la suerte'}
      </button>

      <p className={`hc-slot-meaning${resultado ? ' visible' : ''}`} aria-live="polite">
        {resultado
          ? <>Salió el <b>{numero}</b> — y el {String(dosUltimos).padStart(2, '0')} es <b>{TABLA_SUENOS[dosUltimos]}</b> en la tabla de los sueños</>
          : 'La suerte espera tu giro'}
      </p>

      <small className="hc-slot-disclaimer">Solo para divertirse. Jugá con responsabilidad, +18.</small>
    </div>
  );
}

/* ───────────────────── Variante A · La palanca ───────────────────── */
function DisparadorPalanca({ onAbrir, oculto }) {
  const [tirando, setTirando] = useState(false);
  const digito = useDigitoEnReposo(!oculto && !tirando);

  const tirar = () => {
    if (tirando) return;
    if (movimientoReducido()) { onAbrir(); return; }
    setTirando(true);
    setTimeout(() => { setTirando(false); onAbrir(); }, 420);
  };

  return (
    <button
      type="button"
      className={`ns-trigger ns-palanca${tirando ? ' tirando' : ''}${oculto ? ' oculto' : ''}`}
      onClick={tirar}
      aria-label="Abrir la máquina de la suerte"
    >
      <span className="ns-pal-mecanismo" aria-hidden="true">
        <span className="ns-pal-bola" />
        <span className="ns-pal-brazo" />
        <span className="ns-pal-base" />
      </span>

      <span className="ns-pal-ventana" aria-hidden="true">
        <span className="ns-pal-digito">{digito}</span>
      </span>

      <span className="ns-pal-rotulo" aria-hidden="true">SUERTE</span>
    </button>
  );
}

/* ───────────────────── Variante B · El tambor ────────────────────── */
function DisparadorTambor({ onAbrir, oculto }) {
  const digito = useDigitoEnReposo(!oculto);

  return (
    <button
      type="button"
      className={`ns-trigger ns-tambor${oculto ? ' oculto' : ''}`}
      onClick={onAbrir}
      aria-label="Abrir la máquina de la suerte"
    >
      <span className="ns-tam-aro" aria-hidden="true">
        <span className="ns-tam-ventana">
          <span className="ns-tam-digito">{digito}</span>
        </span>
      </span>
      <span className="ns-tam-rotulo" aria-hidden="true">Tu número</span>
    </button>
  );
}

/* ───────────────────── Variante C · El cospel ──────────────────────
   Ficha con espesor real: dos caras separadas por COS_ESPESOR y un canto
   hecho de segmentos planos tangentes al cilindro. Las rayas alternadas
   del canto son las de una ficha de verdad, y hacen legible el giro.    */
const COS_RADIO = 38;      // px
const COS_ESPESOR = 13;    // px
const COS_SEGMENTOS = 44;  // cuantos más, más redondo el canto

function DisparadorCospel({ onAbrir, oculto }) {
  const arco = (2 * Math.PI * COS_RADIO) / COS_SEGMENTOS;

  return (
    <button
      type="button"
      className={`ns-trigger ns-cospel${oculto ? ' oculto' : ''}`}
      onClick={onAbrir}
      aria-label="Abrir la máquina de la suerte"
    >
      <span className="ns-cos-piso" aria-hidden="true" />
      <span className="ns-cos-escena" aria-hidden="true">
        <span className="ns-cos-ficha">
          <span className="ns-cos-cara ns-cos-frente"><b>777</b></span>
          <span className="ns-cos-cara ns-cos-dorso"><b>777</b></span>

          {Array.from({ length: COS_SEGMENTOS }, (_, i) => (
            <span
              key={i}
              className={`ns-cos-canto${i % 6 < 2 ? ' marca' : ''}`}
              style={{
                width: `${COS_ESPESOR}px`,
                height: `${arco + 1.4}px`,
                marginTop: `${-(arco + 1.4) / 2}px`,
                marginLeft: `${-COS_ESPESOR / 2}px`,
                transform: `rotateZ(${i * (360 / COS_SEGMENTOS)}deg) translateX(${COS_RADIO}px) rotateY(90deg)`,
              }}
            />
          ))}
        </span>
      </span>
      <span className="ns-cos-rotulo" aria-hidden="true">Probá tu suerte</span>
    </button>
  );
}

/* ─────────────────────────── El modal ────────────────────────────── */
function ModalSuerte({ onCerrar, girarAlAbrir }) {
  const panelRef = useRef(null);
  const cerrarRef = useRef(null);

  useEffect(() => {
    cerrarRef.current?.focus();

    const previo = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const alTeclear = (e) => {
      if (e.key === 'Escape') { onCerrar(); return; }
      if (e.key !== 'Tab') return;

      const foco = panelRef.current?.querySelectorAll(
        'button:not([disabled]), a[href], input, [tabindex]:not([tabindex="-1"])'
      );
      if (!foco || !foco.length) return;

      const primero = foco[0];
      const ultimo = foco[foco.length - 1];
      if (e.shiftKey && document.activeElement === primero) {
        e.preventDefault(); ultimo.focus();
      } else if (!e.shiftKey && document.activeElement === ultimo) {
        e.preventDefault(); primero.focus();
      }
    };

    document.addEventListener('keydown', alTeclear);
    return () => {
      document.removeEventListener('keydown', alTeclear);
      document.body.style.overflow = previo;
    };
  }, [onCerrar]);

  return (
    <div className="ns-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) onCerrar(); }}>
      <div className="ns-panel" ref={panelRef} role="dialog" aria-modal="true" aria-labelledby="ns-titulo">
        <div className="ns-panel-head">
          <div>
            <p className="ns-kicker">Para divertirse</p>
            <h2 id="ns-titulo">Tu número de la suerte</h2>
          </div>
          <button type="button" className="ns-cerrar" onClick={onCerrar} ref={cerrarRef} aria-label="Cerrar">
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" fill="none" />
            </svg>
          </button>
        </div>

        <p className="ns-bajada">
          Girá la máquina y llevate un número para la quiniela, con su significado de la tabla de los sueños.
        </p>

        <Maquina girarAlAbrir={girarAlAbrir} />
      </div>
    </div>
  );
}

/* ── Selector de variantes: es temporal, para elegir cuál queda ────── */
function SelectorVariantes({ variante, setVariante }) {
  return (
    <div className="ns-selector">
      <span className="ns-selector-tag">Probando variantes</span>
      <div className="ns-selector-botones">
        {VARIANTES.map((v) => (
          <button
            key={v}
            type="button"
            className={v === variante ? 'activo' : ''}
            onClick={() => setVariante(v)}
          >
            {v}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ──────────────────────────── Público ────────────────────────────── */
export default function NumeroSuerte({ conSelector = true }) {
  const [variante, setVariante] = useState(() => {
    const q = new URLSearchParams(window.location.search).get('suerte');
    return VARIANTES.includes(q) ? q : 'cospel';
  });
  const [abierto, setAbierto] = useState(false);
  const disparadorRef = useRef(null);

  const abrir = () => setAbierto(true);
  const cerrar = useCallback(() => {
    setAbierto(false);
    disparadorRef.current?.querySelector('button')?.focus();
  }, []);

  const elegirVariante = (v) => {
    setVariante(v);
    const url = new URL(window.location.href);
    url.searchParams.set('suerte', v);
    window.history.replaceState({}, '', url);
  };

  const Disparador =
    variante === 'tambor' ? DisparadorTambor :
    variante === 'cospel' ? DisparadorCospel :
    DisparadorPalanca;

  return (
    <>
      <div ref={disparadorRef}>
        <Disparador onAbrir={abrir} oculto={abierto} />
      </div>
      {abierto && <ModalSuerte onCerrar={cerrar} girarAlAbrir={variante !== 'tambor'} />}
      {conSelector && <SelectorVariantes variante={variante} setVariante={elegirVariante} />}
    </>
  );
}
